import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailRow, withStyles } from 'vopay';
import useAccountDocuments from '@/components/Entities/hooks/useApi/useAccountDocuments';
import { useToast } from '@/providers/Toast.provider';
import styles from './FlowOfFundsRow.module.scss';

const cn = withStyles(styles);

/** The stored `VIOAccountDocuments.DocumentType` for an account's flow-of-funds diagram. */
const FLOW_OF_FUNDS = 'flowoffunds';

/**
 * The Flow of Funds row (spec 5.6.1) for `DetailRows`, or null. Most accounts have no diagram on
 * file, so the row exists only once the account's documents come back with one — an empty row would
 * read as missing data. `accountToken` is the account's own bearer; the document routes authenticate
 * as the account, not the partner.
 */
export default function useFlowOfFundsRow(accountToken: string): DetailRow | null {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.DetailsTab.AccountInfo' });
  const { fetchAccountDocuments, fetchDocumentUrl } = useAccountDocuments();
  const toast = useToast();
  const [flowOfFunds, setFlowOfFunds] = useState<{ name: string; url: string }>();

  useEffect(() => {
    // Cleared first: the page is not remounted when the account changes, so the previous account's
    // document would otherwise stay on screen — and stay linked — while this one is looked up.
    setFlowOfFunds(undefined);

    if (!accountToken) return;

    // The list carries no URL, so the first document's own read follows it. Both are resolved before
    // the row renders, which keeps the link a real anchor rather than a click that opens a tab late.
    const loadFlowOfFunds = async () => {
      try {
        const documents = await fetchAccountDocuments(FLOW_OF_FUNDS, accountToken);
        const [first] = documents;

        if (!first) return;

        setFlowOfFunds({
          // The route may send the name null or not at all; the row's own label stands in.
          name: first.DocumentName || t('flowOfFunds', { defaultValue: 'Flow of Funds' }),
          url: await fetchDocumentUrl(first.DocumentID, accountToken),
        });
      } catch (error) {
        // The hook has logged the cause; the row stays hidden, so the toast is the only signal.
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(
          t('flowOfFundsError', {
            defaultValue: 'Could not load the flow of funds document: {{errorMessage}}',
            errorMessage,
          }),
        );
      }
    };

    loadFlowOfFunds();
  }, [accountToken]);

  if (!flowOfFunds) return null;

  return {
    label: t('flowOfFunds', { defaultValue: 'Flow of Funds' }),
    'data-testid': 'detail-flowOfFunds',
    value: (
      <a href={flowOfFunds.url} target="_blank" rel="noopener noreferrer" className={cn('link')}>
        {flowOfFunds.name}
      </a>
    ),
  };
}
